import axios from "axios";
import Vue from "vue";
import api from "./api";
import store from "@/store";
import { Language } from "@/plugins/sharedStaticData";
import { i18n } from "@/main";
// 创建一个独立的axios实例

export const createService = (timeout = 30000) =>
  axios.create({
    // 设置baseUr地址,如果通过proxy跨域可直接填写base地址
    baseURL: api,
    // 配置请求超时时间
    timeout,
    // 如果用的JSONP，可以配置此参数带上cookie凭证，如果是代理和CORS不用设置
    //withCredentials: true
    transformResponse: [
      function (data) {
        if (typeof data === "string") {
          try {
            const MAX_SAFE = 9007199254740991;
            const MIN_SAFE = -9007199254740991;

            // 更精确的正则：匹配 JSON 值中的数字（不在字符串内）
            // 匹配模式：冒号或逗号或开头 + 可选空格 + 数字 + 可选空格 + 逗号或结尾
            const numberRegex =
              /([:{,]\s*)(-?\d{16,}(?:\.\d+)?(?:[eE][+-]?\d+)?)(\s*[},])/g;

            const resultJson = data.replace(
              numberRegex,
              (match, prefix, number, suffix) => {
                const numValue = Number(number);

                // 检查是否超出安全范围
                if (numValue > MAX_SAFE || numValue < MIN_SAFE) {
                  console.log("超出安全范围:", `${prefix}"${number}"${suffix}`);
                  // 将数字用引号包裹
                  return `${prefix}"${number}"${suffix}`;
                }
                // 安全范围内的数字保持不变
                return match;
              }
            );

            // 如果没有任何变化，直接使用原始解析
            if (resultJson === data) {
              return JSON.parse(data);
            }

            console.log("大数字已转换为字符串");
            return JSON.parse(resultJson);
          } catch (error) {
            console.warn("JSON parse error:", error);
            try {
              return JSON.parse(data);
            } catch (e) {
              return data;
            }
          }
        }
        return data;
      },
    ],
  });

const service = createService();

// 请求拦截
service.interceptors.request.use(
  (config) => {
    config.requestStartTime = Date.now();

    const token = window.localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = token;
    }

    const localLang = window.localStorage.getItem("OMS-local");
    if (localLang) {
      const temp = Language.find((item) => item.value == localLang);
      config.headers.languageType = temp ? temp.number : "0";
    } else {
      config.headers.languageType = "0";
    }

    const cleanParams = (obj) => {
      if (!obj) return obj;
      // 字符串 - 清洗双引号
      if (typeof obj === "string") {
        let cleaned = obj;
        while (cleaned.startsWith('"') && cleaned.endsWith('"')) {
          cleaned = cleaned.slice(1, -1);
        }
        cleaned = cleaned.replace(/\\"/g, '"');
        return cleaned;
        // return obj;
      }
      // 时间对象 - 保持不变
      if (obj instanceof Date) {
        return obj;
      }
      // 数组 - 递归处理
      if (Array.isArray(obj)) {
        return obj.map((item) => cleanParams(item));
      }
      // 对象 - 递归处理
      if (typeof obj === "object") {
        const result = {};
        for (const key in obj) {
          result[key] = cleanParams(obj[key]);
        }
        return result;
      }

      return obj;
    };

    // 清洗所有参数 - 跳过文件上传请求
    if (config.params) config.params = cleanParams(config.params);
    if (config.data && !(config.data instanceof FormData)) {
      config.data = cleanParams(config.data);
    }
    return config;
  },
  (error) => {
    console.error("Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// 显示错误提示的统一方法
const showErrorDialog = (message, title = "系统通知", options = {}) => {
  if (window._errorDialogTimer) return;

  Vue.prototype.$confirm(message, title, {
    confirmButtonText: options.confirmButtonText || "确定",
    showCancelButton: false,
    showClose: false,
    closeOnClickModal: false,
    closeOnPressEscape: false,
    customClass: options.customClass || "confirm",
    type: options.type || "error",
    ...options,
  });

  window._errorDialogTimer = setTimeout(() => {
    window._errorDialogTimer = null;
  }, 500);
};

// 处理授权过期
const handleAuthExpired = () => {
  Vue.prototype
    .$confirm("无效的授权信息或授权信息已过期，请重新登录！", "系统通知", {
      confirmButtonText: "跳转登录页面",
      showCancelButton: false,
      showClose: false,
      closeOnClickModal: false,
      closeOnPressEscape: false,
      customClass: "confirm",
      type: "error",
    })
    .then(() => {
      store.dispatch("outLogin");
      const isManagementSide =
        window.localStorage.getItem("server") === "managementSide";
      window.location.href = isManagementSide ? "/login/admin" : "/login";
    })
    .catch(() => {
      const isManagementSide =
        window.localStorage.getItem("server") === "managementSide";
      window.location.href = isManagementSide ? "/login/admin" : "/login";
    });
};

// 返回拦截
service.interceptors.response.use(
  (response) => {
    if (response.message === "终止请求") {
      return Promise.reject(new Error("请求已终止"));
    }

    const responseData = response.data;

    // 处理字符串类型的特殊响应
    if (typeof responseData === "string") {
      if (responseData === "无效的授权信息或授权信息已过期，请重新登录！") {
        handleAuthExpired();
        return Promise.reject(new Error(responseData));
      }
      if (responseData === "禁止访问，没有权限！") {
        showErrorDialog("禁止访问，没有权限！");
        return Promise.reject(new Error(responseData));
      }
    }

    if (response.status === 200) {
      if (response.headers["x-new-token"]) {
        window.localStorage.setItem(
          "token",
          "Bearer " + response.headers["x-new-token"]
        );
      }
      return Promise.resolve(responseData);
    }

    showErrorDialog(
      "接口通讯失败，请尝试刷新页面或者重新登录。如果还是存在问题，请联系管理员"
    );
    return Promise.reject(
      new Error(`HTTP ${response.status}: ${response.statusText}`)
    );
  },
  (error) => {
    console.error("Response error:", error);

    const config = error.config || {};
    const requestStartTime = config.requestStartTime;
    const requestDuration = requestStartTime
      ? Date.now() - requestStartTime
      : 0;

    if (error.code === "ERR_NETWORK") {
      const isNetworkIssue = requestDuration < 300;
      const errorMsg = isNetworkIssue
        ? "网络连接异常，请检查本地网络后重试"
        : "服务器连接超时，请稍后再试";
      //网络错误
      Vue.prototype.$confirm(
        `${errorMsg}\n[error]: ${error.message}`,
        "系统通知",
        {
          confirmButtonText: "确定",
          type: "error",
        }
      );
    } else if (
      typeof error.message === "string" &&
      error.message.includes("timeout of") &&
      error.message.includes("exceeded")
    ) {
      //接口请求超时
      Vue.prototype.$confirm(`服务器繁忙，请稍后刷新重试。`, "系统通知", {
        confirmButtonText: "确定",
        type: "error",
      });
    } else if (error.response.status > 399 && error.response.status < 500) {
      Vue.prototype.$confirm(
        `${i18n.t(
          "Required_Fields_Are_Incomplete_Communication_Failed"
        )}\n[error]: ${error.message}`,
        i18n.t("systematic_notification"),
        {
          confirmButtonText: i18n.t("determine"),
          type: "error",
        }
      );
    } else {
      Vue.prototype.$confirm(
        `${i18n.t(
          "Interface_Communication_Failed_Please_Try_Refreshing_The_Page_Or_Logging_In_Again"
        )}${i18n.t(
          "If_The_Problem_Persists_Please_Contact_The_Administrator"
        )}\n[error]: ${error.message}`,
        i18n.t("systematic_notification"),
        {
          confirmButtonText: i18n.t("determine"),
          type: "error",
        }
      );
    }
    return error;
  }
);

export default service;
